import React, { useState, useEffect } from 'react';
import { Camera, X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Wand2, Plus, ArrowRight, ArrowLeft, ShieldAlert, Zap, Lock, ExternalLink } from 'lucide-react';
import { CreativeBrief, CampaignConcept, CampaignImage, AppStep, GenderPreference, IdentityAnchors } from './types';
import { generateCampaignConcepts, generateTwinImage } from './services/geminiService';

declare global {
  var aistudio: {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  };
}

const REALISM_PROFILES = [
  "Standard Studio", "Raw Cinema", "Ultra-HD Commercial", "Analog Film", "Hyper-Realistic", "Shot on iPhone", "Soft Focus Editorial", "Candid Street", "High-Contrast Noir", "Magazine Print", "80mm Prime", "Vintage Polaroid"
];

const LIGHTING_OPTIONS = [
  "Golden Hour (Soft, Warm)", "Rembrandt (Dramatic Shadow)", "Butterfly (Glamour)", "High Key (Bright, Clean)", "Low Key (Dark, Moody)", "Neon / Cyberpunk", "Softbox Studio", "Natural Window Light", "Hard Flash (Street Style)", "Cinematic Blue Hour"
];

const LENS_OPTIONS = [
  "85mm Prime (Portrait Bokeh)", "35mm (Natural, Classic)", "24mm Wide (Dramatic)", "50mm Standard", "135mm (Compressed)", "Anamorphic Cinematic", "Fisheye (Experimental)", "Macro (Hyper-Detail)"
];

const BODY_TYPE_OPTIONS = ["As in photo", "Slim / Lean", "Athletic / Toned", "Curvy / Hourglass", "Muscular / Built", "Statuesque / Tall", "Petite", "Natural / Average"];

const SCENE_OPTIONS = [
  "Sun-drenched Minimalist Home Office", "Aesthetic Co-working Space (Brooklyn Loft)", "Modern Library", "High-End Coffee Shop", 
  "Industrial Studio", "Zen Garden Workspace", "Boutique Hotel Lobby", "Tech Conference Stage", "Sunset Rooftop Terrace",
  "Executive Airport Lounge", "Minimalist Bedroom", "Modern Kitchen Island", "Mediterranean Villa Poolside", 
  "Brutalist Architecture Lounge", "Designer Furniture Showroom", "Art Collector's Gallery", "Luxury Boardroom",
  "Modernist Desert House", "Glass-walled Forest Cabin", "High-Rise Balcony Tokyo", "Upscale Bookstore",
  "Modern Gym", "Personal Walk-in Closet", "Coastal Cliffside Deck", "Modern Art Museum", "First Class Cabin",
  "Luxury Spa", "Chic Parisian Cafe", "Mid-Century Modern Study", "Minimalist Art Gallery", "Private Jet Cabin"
];

const FEMALE_OPTIONS = {
  vibe: ["Standard / Realistic", "That Girl", "Rich Mom Energy", "Baddie", "Y2K Aesthetic", "Old Money", "Quiet Luxury", "Professional", "Vacation", "Cyberpunk", "Minimalist"],
  scene: SCENE_OPTIONS,
  outfit: [
    "As in photo", "Designer Look", "Casual Chic", "Luxury", "Gym Outfit", "Work Outfit", "Satin Slip Dress", "Power Suit", "Tennis Skirt",
    "Oversized Blazer & Jeans", "Vintage Leather Jacket", "Boho Maxi Dress", "Trench Coat & Turtleneck", "Athleisure Set", "Y2K Low-Rise Denim", 
    "Cottagecore Dress", "Streetwear Cargo Pants", "Evening Gown", "Cocktail Dress", "Business Casual", "Winter Coat & Scarf", "Summer Sundress", 
    "Bikini & Cover-up", "One-Piece Swimsuit", "Lingerie Set", "Silk Pajamas", "Cozy Knit Sweater", "Denim on Denim", "Puffer Jacket & Leggings", 
    "Gothic Grunge", "Preppy Cardigan", "Fairycore Aesthetic", "Cyberpunk Techwear", "Retro 70s Flare", "90s Minimalist", "High-Fashion Avant-Garde", 
    "Bridal Gown", "Bridesmaid Dress", "Maternity Wear", "Festival Outfit", "Ski Suit", "Equestrian Riding Gear", "Golf Attire", "Yoga Pants & Sports Bra", 
    "Ballet Core", "Dark Academia", "Light Academia", "Soft Girl Aesthetic", "Edgy Biker Chic", "Glamorous Sequin Dress", "Velvet Evening Wear", 
    "Linen Summer Suit", "Chunky Knit & Boots", "Plaid Flannel & Jeans", "Raincoat & Boots", "Safari Explorer", "Vintage Pin-up", "Cheerleader Uniform", 
    "Nurse Scrubs", "Flight Attendant Uniform", "Chef Coat", "Police Uniform", "Military Fatigues", "Astronaut Suit", "Superhero Costume", "Fantasy Armor", 
    "Victorian Corset Dress", "Kimono", "Hanbok", "Sari", "Cheongsam", "Dirndl", "Flamenco Dress", "Hula Skirt"
  ],
  hair: ["As in photo", "Half-Up Half-Down", "Sleek Blowout", "Slick Back Bun", "Soft Waves", "High Ponytail"],
  makeup: ["As in photo", "Soft Glam", "Natural / No-Makeup", "Editorial Red Lip", "Glowy Dewy"],
  anatomicalLabels: { makeup: "Makeup Palette", bust: "Bust Line", waist: "Waist Ratio", hips: "Hips Sculpt" },
  anatomicalValues: { bust: ["As in photo", "Slim", "Enhanced", "Natural"], waist: ["As in photo", "Snatched", "Slim", "Realistic"], hips: ["As in photo", "Hourglass", "Curvy", "Athletic"] }
};

const MALE_OPTIONS = {
  vibe: ["Executive / Power", "Old Money", "Gorpcore", "Rugged", "Minimalist", "Professional / CEO", "Vacation / Elite", "Cyberpunk", "Fitness / Bodybuilder"],
  scene: SCENE_OPTIONS,
  outfit: [
    "As in photo", "Power Suit", "Tailored Three-Piece", "Cashmere & Chinos", "Luxury Techwear", "Linen Trousers", "Workout Compressions", "Leather Biker Jacket",
    "Oversized Streetwear", "Vintage Denim Jacket", "Trench Coat & Turtleneck", "Athleisure Set", "Y2K Baggy Jeans", "Lumberjack Flannel", "Cargo Pants & Hoodie", 
    "Tuxedo", "Business Casual", "Winter Coat & Scarf", "Summer Shorts & Polo", "Swim Trunks", "Silk Pajamas", "Cozy Knit Sweater", "Denim on Denim", 
    "Puffer Jacket & Joggers", "Gothic Grunge", "Preppy Cardigan & Tie", "Cyberpunk Techwear", "Retro 70s Suit", "90s Minimalist", "High-Fashion Avant-Garde", 
    "Groom Wedding Suit", "Groomsman Suit", "Festival Outfit", "Ski Suit", "Equestrian Riding Gear", "Golf Attire", "Gym Tank & Shorts", "Dark Academia", 
    "Light Academia", "Edgy Biker Chic", "Velvet Smoking Jacket", "Linen Summer Suit", "Chunky Knit & Boots", "Raincoat & Boots", "Safari Explorer", 
    "Vintage Greaser", "Baseball Uniform", "Basketball Jersey", "Football Kit", "Doctor Scrubs", "Pilot Uniform", "Chef Coat", "Police Uniform", 
    "Military Fatigues", "Astronaut Suit", "Superhero Costume", "Fantasy Armor", "Victorian Frock Coat", "Kimono", "Hanbok", "Kurta", "Kilt", 
    "Lederhosen", "Mariachi Suit", "Ninja Gi"
  ],
  hair: ["As in photo", "Sleek Fade", "Classic Side Part", "Long Textured", "Slick Back", "Buzz Cut"],
  makeup: ["As in photo", "Clean Shaven", "Heavy Stubble", "Full Groomed Beard", "Designer Stubble"],
  anatomicalLabels: { makeup: "Grooming", bust: "Shoulder Width", waist: "V-Taper Ratio", hips: "Physique / Bulk" },
  anatomicalValues: { bust: ["As in photo", "Broad Heroic", "Natural", "Slim Tailored"], waist: ["As in photo", "Lean V-Taper", "Natural", "Athletic"], hips: ["As in photo", "Standard", "Athletic", "Bulky"] }
};

const OPTIONS_MAP = { female: FEMALE_OPTIONS, male: MALE_OPTIONS };

const PhaseHeader: React.FC<{ phase: string; title: string }> = ({ phase, title }) => (
  <div className="flex flex-col items-center gap-2 mb-10 text-center animate-fade-in">
    <span className="text-[8px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase {phase}</span>
    <h2 className="text-xl md:text-2xl font-serif text-white tracking-tight headline-style">{title}</h2>
    <div className="w-6 h-[1px] bg-[#dffe00]/30 mt-3" />
  </div>
);

const StudioDropdown: React.FC<{ label: string; value: string; options: any[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[8px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-3.5 px-5 text-[11px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.06] transition-all cursor-pointer hover:border-white/20"
      >
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          return <option key={val} value={val} className="bg-[#050505]">{lbl}</option>;
        })}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 1024;
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxSize) {
            height *= maxSize / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width *= maxSize / height;
            height = maxSize;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
    };
  });
};

const AnchorSlot: React.FC<{ label: string; value: string | null; onUpload: (data: string | null) => void }> = ({ label, value, onUpload }) => (
  <div className="flex flex-col gap-3 flex-1">
    <label className="text-[8px] font-bold uppercase tracking-widest text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer">
          <Plus className="w-6 h-6 stroke-[1px]" />
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                 const compressed = await compressImage(file);
                 onUpload(compressed);
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

type StudioTab = 'identity' | 'creative' | 'likeness' | 'optics';

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [activeTab, setActiveTab] = useState<StudioTab>('identity');
  const [hasKey, setHasKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [keyError, setKeyError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [brief, setBrief] = useState<CreativeBrief>({
    gender: 'female', vibe: FEMALE_OPTIONS.vibe[0], scene: FEMALE_OPTIONS.scene[0], outfit: FEMALE_OPTIONS.outfit[0],
    hairStyle: FEMALE_OPTIONS.hair[0], makeupPalette: FEMALE_OPTIONS.makeup[0], skinStyle: "Natural & Realistic",
    realismTuning: "Standard Studio", bodyType: "As in photo", bustLine: "As in photo", waistRatio: "As in photo", hipsSculpt: "As in photo",
    cameraAngle: "Standard Eye Level", lighting: LIGHTING_OPTIONS[0], lens: LENS_OPTIONS[0],
    aspectRatio: "9:16", numImages: 4, customPrompt: "", anchorImages: { frontal: null, left: null, right: null }, 
    customLocationImage: null, customAttireImage: null, imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    const checkKey = async () => {
      try {
        const localKey = localStorage.getItem('gemini_api_key');
        if (localKey) {
          setHasKey(true);
          return;
        }

        if (typeof window.aistudio !== 'undefined' && window.aistudio.hasSelectedApiKey) {
          const selected = await window.aistudio.hasSelectedApiKey();
          setHasKey(selected);
        } else {
          setHasKey(false);
        }
      } catch (e) {
        console.warn("Key check failed", e);
        setHasKey(false);
      }
    };
    checkKey();
  }, []);

  const handleManualActivate = () => {
    if (apiKeyInput.trim().length > 5) {
      localStorage.setItem('gemini_api_key', apiKeyInput.trim());
      setHasKey(true);
      setKeyError(false);
    } else {
      setKeyError(true);
    }
  };

  const handleUpdateKey = async () => {
    if (typeof window.aistudio !== 'undefined' && window.aistudio.openSelectKey) {
      await window.aistudio.openSelectKey();
      // Assume success immediately to avoid race condition where hasSelectedApiKey doesn't update fast enough
      setHasKey(true);
      setKeyError(false);
      localStorage.removeItem('gemini_api_key');
    } else {
      const currentKey = localStorage.getItem('gemini_api_key');
      if (currentKey) setApiKeyInput(currentKey);
      setHasKey(false);
    }
  };

  const currentOptions = OPTIONS_MAP[brief.gender];

  const switchGender = (g: GenderPreference) => {
    const nextOptions = OPTIONS_MAP[g];
    setBrief(p => ({
      ...p, gender: g, vibe: nextOptions.vibe[0], scene: nextOptions.scene[0], outfit: nextOptions.outfit[0],
      hairStyle: nextOptions.hair[0], makeupPalette: nextOptions.makeup[0], bustLine: "As in photo",
      waistRatio: "As in photo", hipsSculpt: "As in photo"
    }));
  };

  const handleDownloadAll = () => {
    const completedImages = images.filter(img => img.status === 'completed' && img.imageUrl);
    completedImages.forEach((img, index) => {
      const link = document.createElement('a');
      link.href = img.imageUrl!;
      link.download = `twin-studio-asset-${index + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const initiateProduction = async () => {
    if (!brief.anchorImages.frontal) return alert("Please upload at least a Frontal Identity Anchor.");
    setIsGenerating(true);
    setLoadingMessage("Initializing Neural Fabrication...");

    try {
      setLoadingMessage("Synthesizing Campaign Concepts...");
      const generated = await generateCampaignConcepts(brief);
      
      const initialImages: CampaignImage[] = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, themeIndex: generated[0].themeIndex, status: 'queued'
      }));
      setImages(initialImages);
      
      setTimeout(() => {
        document.getElementById('production-results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

      setLoadingMessage("Rendering High-Fidelity Assets (Sequential Processing)...");

      // Strict Sequential Loop to prevent 503 Overloading
      // We process one image at a time to respect API rate limits and avoid server overload.
      for (const img of initialImages) {
        setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'generating' } : p));
        
        try {
          const url = await generateTwinImage(brief, generated[0].prompt);
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'completed', imageUrl: url } : p));
        } catch (e: any) {
           console.error(`Image ${img.id} failed:`, e);
           if (e.message === "KEY_RESET_REQUIRED") {
             setHasKey(false);
             setKeyError(true);
             localStorage.removeItem('gemini_api_key');
             break; // Stop checking others if key is invalid
           }
           setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'failed', error: e.message } : p));
        }
        
        // Extended pause between generations to help with rate limits
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

    } catch (e: any) {
      if (e.message === "KEY_RESET_REQUIRED") {
        localStorage.removeItem('gemini_api_key');
        setHasKey(false);
        setKeyError(true);
      } else {
        alert(e.message || "Production Error");
      }
    } finally {
      setIsGenerating(false);
      setLoadingMessage("");
    }
  };

  if (!hasKey) {
    const isAiStudio = typeof window.aistudio !== 'undefined' && window.aistudio.openSelectKey;

    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full flex flex-col items-center text-center animate-fade-in space-y-8">
          <div className="relative">
            <div className="w-16 h-16 rounded-[20px] border border-white/10 bg-white/[0.03] flex items-center justify-center backdrop-blur-xl">
              <Lock className="w-6 h-6 text-white/40" />
            </div>
          </div>
          <div className="space-y-3">
             <h1 className="text-3xl md:text-4xl font-serif text-white tracking-tight headline-style">
               AI Twin Studio: <span className="text-[#dffe00]">Pro</span>
             </h1>
             <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-white/30">
               Connect Studio Access Key To Begin
             </p>
          </div>
          <div className="w-full space-y-4 px-4">
             {keyError && (
              <div className="text-red-500 text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-2">
                <ShieldAlert className="w-3 h-3" />
                <span>Invalid or Expired API Key</span>
              </div>
            )}
            
            {isAiStudio ? (
              <button 
                onClick={handleUpdateKey}
                className="w-full bg-white text-black font-bold uppercase tracking-[0.2em] text-[10px] py-4 rounded-xl hover:bg-[#dffe00] transition-colors"
              >
                Select API Key
              </button>
            ) : (
              <>
                <input 
                  type="password"
                  placeholder="Paste Gemini API Key"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-white/10 rounded-xl py-4 px-5 text-white text-center text-[12px] placeholder:text-white/20 focus:outline-none focus:border-white/30 transition-all"
                />
                <button 
                  onClick={handleManualActivate}
                  className="w-full bg-white text-black font-bold uppercase tracking-[0.2em] text-[10px] py-4 rounded-xl hover:bg-[#dffe00] transition-colors"
                >
                  Activate Studio
                </button>
              </>
            )}
          </div>
          <div className="pt-8 border-t border-white/5 w-full flex flex-col items-center gap-4">
            <a 
              href="https://ai.google.dev/gemini-api/docs/billing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[9px] font-bold uppercase tracking-widest text-[#dffe00]/50 hover:text-[#dffe00] transition-colors flex items-center gap-2"
            >
              Billing Documentation <ExternalLink className="w-3 h-3" />
            </a>
            {!isAiStudio && (
              <a 
                href="https://aistudio.google.com/app/api-keys" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 text-white/20 hover:text-white transition-colors group"
              >
                <span className="text-[8px] uppercase tracking-widest">Get your free API Key from Google</span>
                <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100" />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'landing') {
    return (
      <div className="relative min-h-screen bg-[#050505] flex flex-col items-center justify-center p-8 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#dffe00]/5 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative z-10 text-center animate-fade-in max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-serif text-white mb-4 headline-style tracking-[1.5px]">AI Twin Studio: <span className="text-[#dffe00]">Pro</span></h1>
          <p className="text-white/40 uppercase tracking-[0.4em] text-[9px] mb-12">Digital Twin Fabrication & Master Production</p>
          <button 
            onClick={() => setStep('setup')} 
            className="px-12 py-5 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all transform hover:scale-105"
          >
            Enter Studio
          </button>
        </div>
      </div>
    );
  }

  const tabs: {id: StudioTab, label: string}[] = [
    {id: 'identity', label: 'Identity'},
    {id: 'creative', label: 'Creative Direction'},
    {id: 'likeness', label: 'Twin Likeness'},
    {id: 'optics', label: 'Production Optics'}
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#dffe00] selection:text-black pb-32">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-5 flex justify-between items-center bg-gradient-to-b from-[#050505] to-transparent backdrop-blur-md border-b border-white/5">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
          <span className="font-serif text-[10px] tracking-widest headline-style uppercase text-white">
            AI Twin Studio: <span className="text-[#dffe00]">Pro™</span>
          </span>
        </div>
        <button 
          className="text-[8px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors" 
          onClick={() => setStep('landing')}
        >
          Exit Studio
        </button>
      </nav>

      <main className="max-w-4xl mx-auto px-6 pt-32">
        <div className="flex justify-center mb-12 overflow-x-auto scrollbar-hide">
          <div className="flex bg-white/[0.03] p-1 rounded-full border border-white/10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2.5 rounded-full text-[9px] font-bold uppercase tracking-widest whitespace-nowrap transition-all duration-300 ${activeTab === tab.id ? 'bg-[#dffe00] text-black shadow-lg shadow-[#dffe00]/10' : 'text-white/40 hover:text-white'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[400px]">
          {activeTab === 'identity' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="01" title="Identity Anchors" />
              <div className="mb-10 flex bg-white/[0.03] p-1 rounded-full border border-white/10">
                {['female', 'male'].map((g) => (
                  <button key={g} onClick={() => switchGender(g as GenderPreference)} className={`px-6 py-2.5 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all ${brief.gender === g ? 'bg-[#dffe00] text-black' : 'text-white/40 hover:text-white'}`}>
                    For {g === 'female' ? 'Her' : 'Him'}
                  </button>
                ))}
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-5">
                <AnchorSlot label="Frontal (Required)" value={brief.anchorImages.frontal} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, frontal: val } }))} />
                <AnchorSlot label="Left Profile" value={brief.anchorImages.left} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, left: val } }))} />
                <AnchorSlot label="Right Profile" value={brief.anchorImages.right} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, right: val } }))} />
              </div>
              <div className="mt-12 w-full flex justify-end">
                <button onClick={() => setActiveTab('creative')} className="flex items-center gap-2 px-6 py-4 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all">
                  Creative Direction <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'creative' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="02" title="Creative Direction" />
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
                <StudioDropdown label="VIBE" value={brief.vibe} options={currentOptions.vibe} onChange={v => setBrief(p => ({ ...p, vibe: v }))} />
                <div className="flex flex-col gap-6">
                  <StudioDropdown label="LOCATION" value={brief.scene} options={currentOptions.scene} onChange={v => setBrief(p => ({ ...p, scene: v }))} />
                  <AnchorSlot label="Custom Location (Optional)" value={brief.customLocationImage || null} onUpload={val => setBrief(p => ({ ...p, customLocationImage: val }))} />
                </div>
                <div className="flex flex-col gap-6">
                  <StudioDropdown label="ATTIRE" value={brief.outfit} options={currentOptions.outfit} onChange={v => setBrief(p => ({ ...p, outfit: v }))} />
                  <AnchorSlot label="Custom Attire (Optional)" value={brief.customAttireImage || null} onUpload={val => setBrief(p => ({ ...p, customAttireImage: val }))} />
                </div>
              </div>
              <div className="mt-20 w-full flex justify-between">
                <button onClick={() => setActiveTab('identity')} className="flex items-center gap-2 px-6 py-3 text-white/40 hover:text-white text-[9px] font-bold uppercase tracking-widest transition-all">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button onClick={() => setActiveTab('likeness')} className="flex items-center gap-2 px-6 py-4 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all">
                  Twin Likeness <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'likeness' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="03" title="Twin Likeness" />
              <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-8">
                <StudioDropdown label="Signature Hair" value={brief.hairStyle} options={currentOptions.hair} onChange={v => setBrief(p => ({ ...p, hairStyle: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.makeup} value={brief.makeupPalette} options={currentOptions.makeup} onChange={v => setBrief(p => ({ ...p, makeupPalette: v }))} />
                <StudioDropdown label="Skin Dermis" value={brief.skinStyle} options={["Natural & Realistic", "Flawless Airbrushed", "Dewy / Radiant", "Sun-kissed / Tanned"]} onChange={v => setBrief(p => ({ ...p, skinStyle: v }))} />
                <StudioDropdown label="Realism Profile" value={brief.realismTuning} options={REALISM_PROFILES} onChange={v => setBrief(p => ({ ...p, realismTuning: v }))} />
                <StudioDropdown label="Body Type" value={brief.bodyType} options={BODY_TYPE_OPTIONS} onChange={v => setBrief(p => ({ ...p, bodyType: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.bust} value={brief.bustLine} options={currentOptions.anatomicalValues.bust} onChange={v => setBrief(p => ({ ...p, bustLine: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.waist} value={brief.waistRatio} options={currentOptions.anatomicalValues.waist} onChange={v => setBrief(p => ({ ...p, waistRatio: v }))} />
                <StudioDropdown label={currentOptions.anatomicalLabels.hips} value={brief.hipsSculpt} options={currentOptions.anatomicalValues.hips} onChange={v => setBrief(p => ({ ...p, hipsSculpt: v }))} />
              </div>
              <div className="mt-20 w-full flex justify-between">
                <button onClick={() => setActiveTab('creative')} className="flex items-center gap-2 px-6 py-3 text-white/40 hover:text-white text-[9px] font-bold uppercase tracking-widest transition-all">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button onClick={() => setActiveTab('optics')} className="flex items-center gap-2 px-6 py-4 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all">
                  Production Optics <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'optics' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="04" title="Production Optics" />
              <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <StudioDropdown label="Lighting Rig" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({ ...p, lighting: v }))} />
                <StudioDropdown label="Technical Lens" value={brief.lens} options={LENS_OPTIONS} onChange={v => setBrief(p => ({ ...p, lens: v }))} />
                <StudioDropdown label="Camera Angle" value={brief.cameraAngle} options={["Standard Eye Level", "Low Angle Hero", "High Angle Soft", "Side Profile"]} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
                <StudioDropdown 
                  label="Frame Aspect" 
                  value={brief.aspectRatio} 
                  options={[
                    {value:"9:16", label:"9:16 Portrait"}, 
                    {value:"3:4", label:"3:4 Vertical"},
                    {value:"1:1", label:"1:1 Square"}, 
                    {value:"4:3", label:"4:3 Landscape"},
                    {value:"16:9", label:"16:9 Cinematic"}
                  ]} 
                  onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} 
                />
              </div>

              <div className="mt-12 w-full flex flex-col items-center">
                 {isGenerating ? (
                   <div className="w-full max-w-sm flex flex-col items-center gap-4 py-8 bg-white/5 rounded-3xl border border-[#dffe00]/20 shadow-[0_0_30px_rgba(223,254,0,0.1)] animate-pulse">
                      <Loader2 className="w-8 h-8 text-[#dffe00] animate-spin" />
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-[#dffe00] font-bold uppercase tracking-widest text-xs">Production In Progress</span>
                        <span className="text-white/60 text-[10px] uppercase tracking-widest text-center px-4">{loadingMessage}</span>
                      </div>
                   </div>
                 ) : (
                   <button 
                    onClick={initiateProduction} 
                    className="px-16 py-6 bg-[#dffe00] text-black text-[11px] font-bold uppercase tracking-[0.4em] rounded-full hover:bg-white hover:scale-105 transition-all duration-500 shadow-[0_15px_40px_rgba(223,254,0,0.25)]"
                   >
                     Initiate Production
                   </button>
                 )}
              </div>
            </section>
          )}
        </div>

        {(images.length > 0 || isGenerating) && (
          <section id="production-results" className="flex flex-col items-center pt-24 animate-fade-in border-t border-white/5 mt-16 pb-24">
             <div className="w-full flex flex-col md:flex-row md:justify-between items-center gap-6 mb-12">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-serif text-white headline-style">Master Production</h2>
                  {isGenerating && (
                    <span className="flex items-center gap-2 px-3 py-1 bg-[#dffe00]/10 border border-[#dffe00]/20 rounded-full text-[9px] text-[#dffe00] font-bold uppercase tracking-widest">
                       <Zap className="w-3 h-3 fill-current" /> Live
                    </span>
                  )}
                </div>
                {images.some(img => img.status === 'completed') && !isGenerating && (
                  <button 
                    onClick={handleDownloadAll}
                    className="flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-[#dffe00] hover:text-black transition-all group shadow-xl"
                  >
                    <Download className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                    Download All Assets
                  </button>
                )}
             </div>
             <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {images.map(img => (
                  <div key={img.id} className="aspect-[3/4] bg-white/[0.02] border border-white/5 rounded-[24px] overflow-hidden group relative flex items-center justify-center">
                    {img.status === 'completed' ? (
                      <>
                        <img src={img.imageUrl} className="w-full h-full object-cover" alt="Master" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3 backdrop-blur-md">
                          <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-3 bg-white text-black rounded-full"><Maximize2 className="w-4 h-4" /></button>
                          <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'twin.png'; link.click(); }} className="p-3 bg-[#dffe00] text-black rounded-full"><Download className="w-4 h-4" /></button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-3 p-6 text-center w-full">
                         {img.status === 'failed' ? <AlertCircle className="w-6 h-6 text-red-500/50" /> : <Loader2 className="w-6 h-6 text-[#dffe00]/40 animate-spin" />}
                         <span className={`text-[8px] uppercase tracking-widest ${img.status === 'generating' ? 'text-[#dffe00] animate-pulse' : 'text-white/20'}`}>
                           {img.status === 'generating' ? 'Rendering Twin...' : (img.status === 'queued' ? 'Queued' : 'Failed')}
                         </span>
                         {img.status === 'failed' && (
                           <span className="text-[7px] text-red-400 mt-2 px-2 text-center leading-tight">
                             {img.error || 'Generation Failed'}
                           </span>
                         )}
                      </div>
                    )}
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>

      <footer className="w-full max-w-4xl mx-auto px-6 py-8 border-t border-white/5 mt-12 flex justify-center">
        <button 
          onClick={handleUpdateKey}
          className="flex items-center gap-2 text-white/20 hover:text-white transition-colors text-[9px] uppercase tracking-widest font-bold group"
        >
          <Key className="w-3 h-3 group-hover:text-[#dffe00] transition-colors" />
          <span>Update API Key</span>
        </button>
      </footer>

      {previewImage && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} className="max-w-full max-h-full object-contain rounded-xl" alt="Preview" />
        </div>
      )}
    </div>
  );
};

export default App;